import { addDays, isSameDay } from "date-fns";

/**
 * Returns the numeric representation of a day (0 = Sunday, 1 = Monday, ..., 6 = Saturday).
 * Defaults to Monday if the input is invalid.
 *
 * @param dayName - Name of the day (e.g., "Monday", "Tuesday").
 * @returns Day number (0 to 6).
 */
export const getDayNumber = (dayName: string): 0 | 1 | 2 | 3 | 4 | 5 | 6 => {
    const days: Record<string, 0 | 1 | 2 | 3 | 4 | 5 | 6> = {
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
        sunday: 0,
    };
    return days[dayName.toLowerCase()] ?? 1; // Default to Monday
};

/**
 * Calculates the position of a time indicator within a day based on the current time.
 *
 * @param time - The current time as a Date object.
 * @param timeSlotHeight - The time slot grid height
 * @param offsetStartTime
 * @returns The position of the indicator as a string with "px" units.
 */
export const calculateIndicatorPosition = (
  time: Date,
  timeSlotHeight: number,
  offsetStartTime = 0
): number => {
  const MINUTES_IN_HOUR = 60;
  const HOURS_IN_DAY = 24;
  const MINUTES_IN_DAY = MINUTES_IN_HOUR * HOURS_IN_DAY;

  // Convert time to total minutes since start of the day
  const totalMinutes = time.getHours() * MINUTES_IN_HOUR + time.getMinutes();

  // Adjust total minutes based on offsetStartTime
  const offsetMinutes = offsetStartTime / (1000 * MINUTES_IN_HOUR);
  const adjustedMinutes = (totalMinutes - offsetMinutes + MINUTES_IN_DAY) % MINUTES_IN_DAY;

  // Calculate position within the day
  return (adjustedMinutes / MINUTES_IN_DAY) * (timeSlotHeight * HOURS_IN_DAY);
};

/**
 * Calculates the position of today's date relative to a given starting day.
 *
 * @param startingDay - The starting day of the week.
 * @returns The position of today in the week (0 to 6), or -1 if not found.
 */
export const calculateTodayPosition = (startingDay: Date): number => {
    const today = new Date();

    for (let i = 0; i < 7; i++) {
        const currentDay = addDays(startingDay, i);
        if (isSameDay(today, currentDay)) {
            return i;
        }
    }

    return -1; // Default if today is not in the week
};

export const hours = (offsetStartTime = 0) => Array.from({ length: 24 }, (_, i) => {
    const offsetHours = Math.floor(offsetStartTime / (1000 * 60 * 60)); // Convert ms to hours
    const adjustedHour = (i + offsetHours) % 24;
    const hour = adjustedHour % 12 === 0 ? 12 : adjustedHour % 12; // Convert 24-hour format to 12-hour format
    const period = adjustedHour < 12 ? "AM" : "PM";     // Determine AM or PM
    return `${hour} ${period}`;
});

export const getLocalTimeZone = (): string => {
    const offset = new Date().getTimezoneOffset();
    const hours = Math.abs(offset) / 60;
    const minutes = Math.abs(offset) % 60;
    const sign = offset > 0 ? "-" : "+";

    return `GMT ${sign}${Math.floor(hours)}:${minutes.toString().padStart(2, "0")}`;
};

/**
 * Converts a calendar date to the actual date considering the offset
 */
export const getActualDateFromCalendar = (calendarDate: Date, offsetStartTime = 0): Date => {
  const actualDate = new Date(calendarDate);
  actualDate.setTime(actualDate.getTime() + offsetStartTime);
  return actualDate;
};


export const generateSkeletonVisibility = (
    hoursLength: number,
    seed: number
): boolean[] => {
    let lastSkeletonHour = -4;
    const visibilityArray = new Array(hoursLength).fill(false);

    // Seed-based randomization for different days
    const seededRandom = (hourIndex: number) => {
        return Math.abs(Math.sin((hourIndex + seed) * 1234)) % 1; // Seed affects randomization
    };

    for (let i = 0; i < hoursLength; i++) {
        if (i - lastSkeletonHour >= 6 && seededRandom(i) > 0.8) {
            visibilityArray[i] = true;
            lastSkeletonHour = i;
        }
    }

    return visibilityArray;
};

export const getUpdatedStackedShifts = (hourPassingShifts: any[], processedShifts: any[]) => {
    try {
        return hourPassingShifts?.reduce((acc, shift) => {
            const shiftIndex = processedShifts?.findIndex(
                (processedShift) => processedShift.id === shift.id
            );
            if (shiftIndex !== -1) {
                acc.push(processedShifts[shiftIndex]);
            }
            return acc;
        }, []);
    } catch (e) {
        return hourPassingShifts;
    }
}
export const canSelectTime = (dayIndex: number, todayPosition: number, currentTime: number, yPosition: number) => {
  try {
    // If the current time is not in past for today, allow selection
    if (dayIndex === todayPosition && currentTime < yPosition) {
      return true;
    } else if (dayIndex > todayPosition) {
      // If we are ahead of today, allow selection
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
}
/**
 * Adjusts a date to the calendar date considering offset, ensuring proper day placement.
 */
export const getShiftEffectiveStartDay = (shiftStart: Date, offsetStartTime = 0): Date => {
  const adjustedDate = new Date(shiftStart.getTime() - offsetStartTime);

  // Normalize hour to prevent shifting beyond valid ranges
  const offsetInHours = offsetStartTime / (1000 * 60 * 60);
  const normalizedHour = (shiftStart.getHours() - offsetInHours + 24) % 24;

  adjustedDate.setHours(normalizedHour, 0, 0, 0);

  // If the resulting hour is less than 0, move to the previous day
  if (normalizedHour < 0) {
    adjustedDate.setDate(adjustedDate.getDate() - 1);
  }

  return adjustedDate;
};